import axios from 'axios';

import { QUICK_CASHFLOW_ALLOWED_MODELS } from './quickCashflowAiConfigApi';

// Mirrors the backend's default templates (entities/mailintake package) as a client-side
// fallback for the "edit" form before the first successful load, matching the pattern used
// in quickCashflowAiConfigApi.js.
export const DEFAULT_CUSTOMER_MATCH_PROMPT = `You are matching an inbound email to the company ("customer") it's from/about, out of a fixed list of candidates. None of our deterministic rules (exact/domain email match, exact company name match) found a match, so you are the last resort.

Rules you must follow, strictly and literally:
1. Ignore the domain "twelvegrow.com" and the name "Treisar" completely — never use either as a signal for identifying the customer, even if they appear in the From/To/Cc/Bcc addresses or the body. They are internal/organizational, never the customer.
2. Only treat a name found in the email as matching a candidate if it is EXACTLY the same (case-insensitive), or differs by AT MOST ONE character (e.g. one letter changed, added, or missing — a single typo). Count literally, character by character. Two or more differing characters is NOT a match, no matter how similar the names look or sound.
3. Do not guess or infer a match based on names seeming related, similar, or belonging to people/companies that might be connected (e.g. same surname, same industry, plausible family or business relation). Only literal near-identical spelling counts — nothing else. For example, "Olena Turevska" and "Alex Turevski" are NOT a match: they are different first names, and differ in far more than one character — the fact that the surnames are similar and the people might be related is irrelevant and must NOT be used as a basis for matching.
4. Only compare candidate names against text that identifies the COMPANY/CUSTOMER itself (e.g. "attached financials for Acme Inc", a company name in a subject line or signature block). Never compare a candidate's name against the name of an individual PERSON — a sender, a signer, someone cc'd, someone mentioned in passing — even if that person's name happens to resemble a candidate's name. A coincidental resemblance between a candidate's name and a human being's name is never a match. If the only name-like text in the email is a person's name and there is no separate company/customer name mentioned, do not attempt a name match at all.
5. If you are not certain a candidate satisfies rules 1-4, set matched=false. A false negative here is safe (the email is simply flagged for manual review) — a false positive is not, so always resolve any doubt toward matched=false.

Candidates (id and name — you may ONLY return one of these exact ids, never invent one):
{{candidates}}
Email data (attachments are NOT included):
Subject: {{subject}}
From: {{from}}
To: {{to}}
Cc: {{cc}}
Bcc: {{bcc}}
Body:
{{body}}

Decide which candidate (if any) this email is from/about, following the rules above exactly. Set matched=true and customerId/customerName to your candidate ONLY if rules 1-5 are all satisfied — otherwise set matched=false, customerId=null, customerName=null. Always explain your reasoning in "reason", including the literal character-by-character comparison you made if a name was involved, and confirm whether the compared text was a company/customer name (not a person's name).`;

export const DEFAULT_FINANCIAL_SNAPSHOT_PROMPT = `The customer for this email has already been identified as customerId="{{customerId}}", customerName="{{customerName}}". Echo these two values exactly as given in the customerId and customerName fields of your output.

You are analyzing financial documents (P&L, balance sheet, cap table, cash flow statement, bank statements, or investor updates) attached to an email from a company. Extract the following fields as of the most recent reporting period available in the attached documents. For every field, if the value cannot be determined from the attached documents, set "value" (or "amount"/"hasSafe" for safe) to null and explain why in "reason".

{{fieldInstructions}}

For every field (including cohortData), also set "sourceDocument" to the exact filename — copied verbatim from the "Attached files" list below — of the document that value was drawn from. If a field draws on more than one document, name the single most relevant one. If the value is null/unknown, set "sourceDocument" to the filename you checked and found nothing relevant in (or "" only if no attachment was even plausibly relevant).

Also provide documentSummary: a 1-2 sentence summary of what documents were reviewed and their reporting period(s).

Also provide attachmentSummaries: exactly one entry per attached file below, with filename matching exactly as given and a one-sentence summary of what that specific document is and what it contains.
Attached files:
{{attachmentList}}

Base every value only on what is explicitly stated or directly computable from the attached documents. Do not guess or infer values that aren't supported by the data.`;

// Field "kind" identifies which fixed JSON schema shape a field uses — mirrors
// mailintake_entities.MailIntakeFinancialSnapshotFieldKind* on the backend. Only 'generic'
// fields (a plain number + reason) can be freely added or removed from this page; 'safe' and
// 'cohortData' are structurally special (multi-property shapes wired into backend parsing
// and cockpit UI rendering) and always have exactly one entry each — editable/toggleable,
// but not removable and not renamable.
export const FIELD_KIND_GENERIC = 'generic';
export const FIELD_KIND_SAFE = 'safe';
export const FIELD_KIND_COHORT_DATA = 'cohortData';

// Mirrors mailintake_entities.DefaultFinancialSnapshotFields() — the field list a fresh
// config is seeded with, used here only as a client-side fallback for the "edit" form before
// the first successful load (matching the pattern used for the two prompts above).
export const DEFAULT_FINANCIAL_SNAPSHOT_FIELDS = [
  {
    key: 'opex',
    label: 'Opex',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction:
      '- opex: Total operating expenses for the most recent reported month or period.',
  },
  {
    key: 'cashBalance',
    label: 'Cash Balance',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction: '- cashBalance: Current (most recent) cash balance.',
  },
  {
    key: 'grossProfitMarginPct',
    label: 'Gross Profit Margin Pct',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction:
      '- grossProfitMarginPct: Gross profit margin as a percentage (0-100) for the most recent period: (Revenue - COGS) / Revenue * 100.',
  },
  {
    key: 'debt',
    label: 'Debt',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction:
      '- debt: Total outstanding debt / loans payable (excluding SAFEs and equity).',
  },
  {
    key: 'totalAssets',
    label: 'Total Assets',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction: '- totalAssets: Total assets from the balance sheet.',
  },
  {
    key: 'totalLiabilities',
    label: 'Total Liabilities',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction: '- totalLiabilities: Total liabilities from the balance sheet.',
  },
  {
    key: 'netBurn',
    label: 'Net Burn',
    kind: FIELD_KIND_GENERIC,
    enabled: true,
    instruction:
      '- netBurn: Net burn = the average month-over-month CHANGE in cash balance over the last 3 reported months (average of (cash[month] - cash[month-1]) across the last 3 months). Report as a negative number when cash is declining (burning cash), positive if cash is growing. If fewer than 3 months of cash data are available, average what\'s available and say so in "reason".',
  },
  {
    key: 'safe',
    label: 'Safe',
    kind: FIELD_KIND_SAFE,
    enabled: true,
    instruction:
      '- safe: Whether the company has issued SAFE (Simple Agreement for Future Equity) notes. Set hasSafe true/false, and amount to the total outstanding SAFE principal if disclosed (null if unknown or none).',
  },
  {
    key: 'cohortData',
    label: 'Cohort Data',
    kind: FIELD_KIND_COHORT_DATA,
    enabled: true,
    instruction: `- cohortData: Customer or revenue cohort data (e.g., retention-by-cohort or revenue-by-cohort data), reported as a structured table, not a text summary:
  - applied: true if the documents contain ANY cohort-structured data — a literal table, a chart/graph with per-cohort/per-period values or data labels, or a list broken out by cohort and period — even if it's not laid out as a clean grid. Do not require a perfectly formatted table: if a document (e.g. one literally named "Cohort" or titled as a cohort analysis) presents cohort performance broken down by period, set applied=true and do your best to extract rows from it, using null for any individual cell you can't determine rather than giving up on the whole field. Only set applied=false if the documents genuinely contain no cohort-by-period breakdown at all.
  - metric: "roas" if period values are cumulative ROAS ratios/percentages, "revenue" if period values are cumulative dollar revenue.
  - rows: one entry per cohort, each with cohortDate (normalize to YYYY-MM when possible), marketingSpend for that cohort (null if not shown), and periodValues (the cumulative value for each period/month-on-book column in order, left to right; null for any missing cell). Populate as many rows and cells as you can find — a partial extraction is much more useful than none.
  - If, after making a genuine effort, no cohort-by-period data is present anywhere in the documents, set applied false, metric "", rows [], and explain specifically why in "reason" (e.g. which documents you checked and what they contained instead).`,
  },
];

// Mirrors mailintake_entities.MailIntakeProvider* / DefaultMailIntakeProvider. Claude is
// the default — a config with no provider set runs on Anthropic; selecting OpenAI here is
// the way back.
export const MAIL_INTAKE_PROVIDER_ANTHROPIC = 'anthropic';
export const MAIL_INTAKE_PROVIDER_OPENAI = 'openai';
export const DEFAULT_MAIL_INTAKE_PROVIDER = MAIL_INTAKE_PROVIDER_ANTHROPIC;

export const MAIL_INTAKE_PROVIDERS = [
  { value: MAIL_INTAKE_PROVIDER_ANTHROPIC, label: 'Anthropic (Claude)' },
  { value: MAIL_INTAKE_PROVIDER_OPENAI, label: 'OpenAI (ChatGPT)' },
];

// Mirrors anthropic.AllowedModels. Opus 5 is the current Opus generation and the default;
// there is no "Opus 5.5". Fable 5.1 is more capable but priced above the Opus tier.
export const ANTHROPIC_ALLOWED_MODELS = [
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-haiku-4-5',
  'claude-fable-5-1',
];

export const DEFAULT_ANTHROPIC_MODEL = 'claude-opus-5';

// Mirrors usecase.mailIntakeAllowedEfforts. Claude only — effort controls thinking depth.
export const ANTHROPIC_EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];

// Mirrors mailintake_entities.MailIntakeAIStages, in the same display order. Each stage of
// the mail-intake pipeline can override the config-level model/effort; leaving a stage on
// "Default" inherits it.
export const MAIL_INTAKE_AI_STAGES = [
  {
    key: 'customerMatch',
    label: 'Customer match',
    description:
      'Last-resort recognition of which customer an email is from. Text only — no attachments are sent.',
  },
  {
    key: 'financialExtract',
    label: 'Financial extract',
    description:
      "Extracts the financial-snapshot fields below from the email's attachments.",
  },
  {
    key: 'imageFilter',
    label: 'Image filter',
    description:
      'Decides whether an attached image holds financial data or is just a signature logo.',
  },
  {
    key: 'attachmentSummary',
    label: 'Attachment summary',
    description:
      'One sentence per new attachment, posted to Slack when files arrive.',
  },
  {
    key: 'qcfAccounting',
    label: 'QCF accounting',
    description:
      'The heavier Quick Cashflow accounting pipeline, run when the financial extract finds data.',
  },
];

export const allowedModelsForProvider = (provider) =>
  provider === MAIL_INTAKE_PROVIDER_ANTHROPIC
    ? ANTHROPIC_ALLOWED_MODELS
    : QUICK_CASHFLOW_ALLOWED_MODELS;

export const defaultModelForProvider = (provider) =>
  provider === MAIL_INTAKE_PROVIDER_ANTHROPIC ? DEFAULT_ANTHROPIC_MODEL : 'gpt-4o';

export const getLatestMailIntakePromptConfig = async () => {
  const response = await axios.get('admin/mail-intake-prompt-config/latest');
  return response.data;
};

export const listMailIntakePromptConfigVersions = async () => {
  const response = await axios.get('admin/mail-intake-prompt-config/versions');
  return response.data;
};

export const getMailIntakePromptConfigVersion = async (version) => {
  const response = await axios.get(
    `admin/mail-intake-prompt-config/versions/${version}`
  );
  return response.data;
};

export const createMailIntakePromptConfigVersion = async (body) => {
  const response = await axios.post('admin/mail-intake-prompt-config', body);
  return response.data;
};
